import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { CollectionWizard } from '../components/CollectionWizard';
import { Icon } from '../components/Icon';
import { IncotermCard } from '../components/IncotermCard';
import { RedlineCheck } from '../components/RedlineCheck';
import { SinosureReportWizard } from '../components/SinosureReportWizard';
import { Toast } from '../components/Toast';
import { WorkflowProgress } from '../components/WorkflowProgress';
import { Badge, Card, TabPanel, Tabs } from '../components/ui';
import { type CollectionTemplateKey, type Order, customerApi, orderApi } from '../lib/api';

export const Route = createFileRoute('/orders/$orderId')({
  component: OrderDetailPage,
});

const STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  deposit_received: '已收定金',
  producing: '生产中',
  qc: '验货中',
  balance_received: '已收尾款',
  shipped: '已装船',
  closed: '已关闭',
  cancelled: '已取消',
};

function fmt(cents: number, cur: string) {
  const amount = cents / 100;
  if (cur === 'JPY') return `\u00a5${amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  return `${cur === 'USD' ? '$' : cur === 'EUR' ? '\u20ac' : cur} ${amount.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

function PaymentStep({
  index,
  title,
  sub,
  done,
  overdue,
  money,
  link,
  overdueDays,
}: {
  index: number;
  title: string;
  sub: string;
  done: boolean;
  overdue?: boolean;
  money?: string | null;
  link?: string;
  overdueDays?: number;
}) {
  const bg = done
    ? 'bg-good text-white'
    : overdue
      ? 'bg-bad text-white'
      : 'bg-surface-2 border border-line text-ink-3';
  return (
    <li className={`flex items-start gap-2 ${overdue ? 'bg-bad/5 -mx-2 px-2 py-1 rounded' : ''}`}>
      <span
        className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${bg}`}
      >
        {done ? '✓' : index}
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-medium flex items-center gap-2 flex-wrap">
          <span>{title}</span>
          {money && <span className="text-xs text-ink-3">— {money}</span>}
          {overdue && (
            // v1.37.9: 逾期天数由调用方算好传入 (不要在这里再算一遍;
            // 之前把 overdueDays=5 当成时间戳未减, 直接显示 Date.now()/86400000 = 20720 天)
              <span className="text-[10px] text-bad font-bold">⚠ 逾期 {overdueDays ?? 0} 天</span>
            )}
        </div>
        <div className="text-xs text-ink-3">
          {sub}
          {link && (
            <>
              {' · '}
              <Link to={link} className="text-accent hover:underline">
                查看
              </Link>
            </>
          )}
        </div>
      </div>
    </li>
  );
}

export function OrderDetailPage() {
  const { orderId } = useParams({ from: '/orders/$orderId' });
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);
  // v1.11: 追讨 / 信保 wizard 控制
  const [showCollection, setShowCollection] = useState(false);
  const [showSinosure, setShowSinosure] = useState(false);
  // v1.32: 移动端 tab (清单 / 单证 / 收款 / 详情)
  const [tab, setTab] = useState<'checklist' | 'docs' | 'payment' | 'detail'>('checklist');
  const {
    data: order,
    isLoading,
    error: orderError,
  } = useQuery<Order>({
    queryKey: ['order', orderId],
    queryFn: () => orderApi.get(orderId),
    retry: false,
  });
  const { data: customers } = useQuery({
    queryKey: ['customers', order?.companyId],
    queryFn: () => customerApi.list({ limit: 200 }),
    enabled: !!order,
  });

  const completeMutation = useMutation({
    mutationFn: ({ stepId }: { stepId: string }) => orderApi.completeStep(orderId, stepId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
    },
    onError: (e) => {
      setToast({ kind: 'error', msg: (e as Error).message });
    },
  });

  const closeMutation = useMutation({
    mutationFn: () => orderApi.update(orderId, { status: 'closed' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', orderId] });
      setToast({
        kind: 'success',
        msg: '\u8ba2\u5355\u5df2\u5173\u95ed, AI \u590d\u76d8\u5df2\u751f\u6210',
      });
    },
    onError: (e) => {
      setToast({ kind: 'error', msg: (e as Error).message });
    },
  });

  function handleClose() {
    if (
      !window.confirm(
        '\u786e\u8ba4\u5173\u95ed\u8ba2\u5355? \u5173\u95ed\u540e\u5c06\u81ea\u52a8\u751f\u6210 AI \u590d\u76d8',
      )
    )
      return;
    closeMutation.mutate();
  }

  if (isLoading) return <div className="text-ink-3 text-sm">\u52a0\u8f7d\u4e2d...</div>;
  if (orderError || !order) {
    return <div className="text-bad text-sm">\u8ba2\u5355\u4e0d\u5b58\u5728</div>;
  }

  const customer = customers?.items.find((c) => c.id === order.customerId);
  const pct = (order.checklistProgress.completedSteps / order.checklistProgress.totalSteps) * 100;
  const isCancelled = order.status === 'cancelled';

  // v1.11: 计算 daysOverdue (用于红色 banner)
  const isOverdueRelevant =
    order.status !== 'closed' &&
    order.status !== 'cancelled' &&
    order.status !== 'balance_received';
  const dueMs = order.etaDate ?? order.shipDate;
  let daysOverdue = 0;
  let isOverdue = false;
  if (isOverdueRelevant && dueMs) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    daysOverdue = Math.floor((today.getTime() - dueMs) / (24 * 60 * 60 * 1000));
    isOverdue = daysOverdue > 0;
  }
  let recommendedStage: CollectionTemplateKey = 'D1_REMINDER';
  if (daysOverdue > 21) recommendedStage = 'D30_FINAL';
  else if (daysOverdue > 10) recommendedStage = 'D14_URGENT';
  else if (daysOverdue > 3) recommendedStage = 'D7_WARNING';

  return (
    <div className="max-w-4xl space-y-4">
      <Link to="/orders" className="text-sm text-accent inline-block mb-1">
        \u2190 \u8fd4\u56de\u8ba2\u5355\u5217\u8868
      </Link>

      {/* v1.11: 红色"客户拖欠" banner + 启动追讨 / 生成信保按钮 */}
      {isOverdue && (
        <div className="bg-bad/10 border border-bad/30 rounded p-3 flex items-start gap-3 flex-wrap">
          <Icon name="siren" size="md" className="text-bad shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-bad">
              \u5ba2\u6237\u5df2\u62d6\u6b20 {daysOverdue} \u5929
            </div>
            <div className="text-xs text-ink-2 mt-0.5">
              \u5e94\u4ed8\u65e5\u671f:{' '}
              {dueMs ? new Date(dueMs).toLocaleDateString('zh-CN') : '\u2014'}.
              \u63a8\u8350\u9636\u6bb5: {recommendedStage}.
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => setShowCollection(true)}
              className="px-2.5 py-1 bg-bad text-white rounded text-xs hover:opacity-90 inline-flex items-center gap-1"
            >
              <Icon name="mail" size="xs" />
              \u542f\u52a8\u8ffd\u8ba9
            </button>
            <button
              type="button"
              onClick={() => setShowSinosure(true)}
              className="px-2.5 py-1 border border-bad text-bad rounded text-xs hover:bg-bad/5 inline-flex items-center gap-1"
            >
              <Icon name="file-text" size="xs" />
              \u751f\u6210\u4fe1\u4fdd\u62a5\u6848
            </button>
          </div>
        </div>
      )}

      {/* v1.32: 顶部摘要 Card (PO + 客户 + 总金额大 + 状态 badge) */}
      <Card padding="md" className="animate-fade-up">
        <div className="flex items-start justify-between mb-3 gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-xs text-ink-3 mb-1 flex items-center gap-1.5">
              <span>\u8ba2\u5355</span>
              <span>\u00b7</span>
              <span>
                {order.incoterm} {order.incotermPlace}
              </span>
            </div>
            <h1 className="text-xl sm:text-2xl font-semibold text-ink leading-tight break-all">
              {order.poNumber || order.id.slice(0, 8)}
            </h1>
            <div className="text-sm text-ink-2 mt-1.5">
              {customer?.name ? (
                <Link
                  to="/customers/$customerId"
                  params={{ customerId: order.customerId }}
                  className="hover:text-accent"
                >
                  {customer.name}
                </Link>
              ) : (
                '...'
              )}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-2xl sm:text-3xl font-bold text-ink tabular-nums">
              {fmt(order.totalAmountCents, order.currency)}
            </div>
            <Badge
              variant={
                order.status === 'closed'
                  ? 'neutral'
                  : order.status === 'cancelled'
                    ? 'error'
                    : order.status === 'shipped' || order.status === 'balance_received'
                      ? 'success'
                      : 'info'
              }
              size="md"
              className="mt-1.5"
            >
              {STATUS_LABELS[order.status] || order.status}
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 text-sm mb-3">
          <div>
            <div className="text-xs text-ink-3 mb-0.5">\u5b9a\u91d1\u6bd4\u4f8b</div>
            <div className="font-medium">{order.depositPct}%</div>
          </div>
          <div>
            <div className="text-xs text-ink-3 mb-0.5">\u88c5\u8239\u65e5</div>
            <div className="font-medium text-xs">
              {order.shipDate ? new Date(order.shipDate).toLocaleDateString('zh-CN') : '\u2014'}
            </div>
          </div>
          <div>
            <div className="text-xs text-ink-3 mb-0.5">checklist</div>
            <div className="font-medium text-xs">
              {order.checklistProgress.completedSteps}/{order.checklistProgress.totalSteps} (
              {Math.round(pct)}%)
            </div>
          </div>
        </div>

        <div
          className="h-1.5 bg-surface-2 rounded overflow-hidden"
          role="progressbar"
          tabIndex={0}
          aria-valuenow={Math.round(pct)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`checklist ${Math.round(pct)}%`}
        >
          <div
            className="h-full bg-accent transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>

        {order.status !== 'closed' && order.status !== 'cancelled' && (
          <div className="mt-3 pt-3 border-t border-line flex justify-end">
            <button
              type="button"
              onClick={handleClose}
              disabled={closeMutation.isPending}
              className="text-xs text-ink-3 hover:text-bad disabled:opacity-50"
            >
              {closeMutation.isPending
                ? '\u5173\u95ed\u4e2d\u2026'
                : '\u5173\u95ed\u8ba2\u5355 + \u81ea\u52a8\u590d\u76d8 \u2192'}
            </button>
          </div>
        )}
      </Card>

      {/* v1.8 P0: 10 \u9636\u6bb5\u8ba2\u5355\u8fdb\u5ea6\u6761 (\u79fb\u52a8\u7aef\u7ad6\u6392 / \u684c\u9762\u6a2a\u6392) */}
      <WorkflowProgress
        status={order.status}
        completedSteps={order.checklistProgress.completedSteps}
        totalSteps={order.checklistProgress.totalSteps}
        cancelled={isCancelled}
      />

      {/* v1.32: 移动端 tab 切换 — 桌面端隐藏 (TabPanel 始终 active) */}
      <div className="sm:hidden">
        <Tabs
          value={tab}
          onChange={(v) => setTab(v as typeof tab)}
          variant="pill"
          ariaLabel="\u8ba2\u5355\u8be6\u60c5\u5206\u533a"
          items={[
            { value: 'checklist', label: '\u6e05\u5355', icon: 'list-checks' },
            { value: 'docs', label: '\u5355\u8bc1', icon: 'file-text' },
            { value: 'payment', label: '\u6536\u6b3e', icon: 'circle-dollar-sign' },
            { value: 'detail', label: '\u8be6\u60c5', icon: 'info' },
          ]}
        />
      </div>

      {/* === Panel 1: \u6e05\u5355 (checklist) === */}
      <TabPanel value="checklist" active={tab === 'checklist'} className="sm:block">
        <div className="mb-4">
          <IncotermCard incoterm={order.incoterm} place={order.incotermPlace} />
        </div>

        <div className="bg-surface border border-line rounded p-5">
          <h2 className="text-sm font-semibold mb-3">
            \ud83d\udccb {order.incoterm} \u6d41\u7a0b\u6e05\u5355\uff08
            {order.checklistProgress.totalSteps} \u6b65\uff09
          </h2>
          <ol className="space-y-2">
            {order.checklist.map((step) => {
              const progress = order.checklistProgress.steps.find((s) => s.stepId === step.id);
              const status = progress?.status ?? 'pending';
              const isDone = status === 'done';
              const isSkipped = status === 'skipped';
              const isCurrent =
                !isDone &&
                !isSkipped &&
                order.checklistProgress.steps[order.checklistProgress.completedSteps]?.stepId ===
                  step.id;

              return (
                <li
                  key={step.id}
                  className={`p-3 border rounded ${
                    isDone
                      ? 'border-line bg-surface-2'
                      : isSkipped
                        ? 'border-line bg-surface-2/50 opacity-60'
                        : isCurrent
                          ? 'border-warn bg-warn/5'
                          : step.blocksShipping && !isDone
                            ? 'border-bad bg-bad/5'
                            : 'border-line'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      onClick={() => !isDone && completeMutation.mutate({ stepId: step.id })}
                      disabled={isDone || completeMutation.isPending}
                      className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center text-sm ${
                        isDone
                          ? 'bg-good border-good text-white'
                          : isCurrent
                            ? 'border-warn text-warn hover:bg-warn hover:text-white'
                            : 'border-line text-ink-3'
                      }`}
                    >
                      {isDone ? '\u2713' : isSkipped ? '\u2013' : step.index}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div
                        className={`font-medium text-sm ${isDone ? 'line-through text-ink-3' : ''}`}
                      >
                        {step.title.zh}
                      </div>
                      <div className="text-xs text-ink-2 mt-0.5">{step.description.zh}</div>
                      <details className="mt-1.5">
                        <summary className="text-[11px] text-accent cursor-pointer hover:underline list-none flex items-center gap-1 select-none">
                          <span>\ud83d\udca1 \u4e3a\u4ec0\u4e48\u8fd9\u6b65\u91cd\u8981</span>
                        </summary>
                        <div className="mt-1.5 text-xs text-ink-2 bg-info/5 border border-info/20 rounded p-2 leading-relaxed">
                          {step.why?.zh ?? step.coachHint.zh}
                        </div>
                      </details>
                      {step.requiredDocs.length > 0 && (
                        <div className="text-xs text-ink-3 mt-1.5">
                          \u9700\u4e0a\u4f20: {step.requiredDocs.join(', ')}
                        </div>
                      )}
                      {step.blocksShipping && !isDone && (
                        <div className="text-xs text-bad mt-1.5">
                          \u26d4 \u98ce\u9669: \u672a\u5b8c\u6210\u4e0d\u80fd\u88c5\u67dc
                        </div>
                      )}
                      {isDone && progress?.completedAt && (
                        <div className="text-xs text-ink-3 mt-1.5">
                          \u2713 \u5b8c\u6210\u4e8e{' '}
                          {new Date(progress.completedAt).toLocaleString('zh-CN')}
                        </div>
                      )}
                      {isCurrent && (
                        <div className="text-xs text-ink-2 mt-2 bg-info/5 border border-info/20 rounded p-2">
                          \ud83e\udd16 {step.coachHint.zh}
                        </div>
                      )}
                      {progress?.note && (
                        <div className="text-xs text-ink-2 mt-1.5">
                          \u5907\u6ce8: {progress.note}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </TabPanel>

      {/* === Panel 2: 单证 (docs) === */}
      <TabPanel value="docs" active={tab === 'docs'} className="sm:block">
        <div>
          <RedlineCheck />
        </div>
        <Card padding="md" className="mt-4 opacity-60">
          <h2 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
            <Icon name="file-text" size="sm" className="text-ink-3" />9
            \u7c7b\u8d38\u6613\u5355\u8bc1 (v1.28)
          </h2>
          <p className="text-xs text-ink-3">
            CI / PL / BL / AWB / CL / CF / COO / FormA / Insurance
            \u751f\u6210\u540e\u4ece\u8d44\u6599\u533a\u4e0b\u8f7d.
          </p>
        </Card>
      </TabPanel>

      {/* === Panel 3: 收款 (payment) === */}
      <TabPanel value="payment" active={tab === 'payment'} className="sm:block">
        <Card padding="md">
          <h2 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
            <Icon name="circle-dollar-sign" size="sm" className="text-accent" />
            \u8d44\u91d1\u6d41 4 \u6b65
          </h2>
          <ol className="space-y-2 text-sm">
            {(() => {
              const now = Date.now();
              const due7 = order.etaDate ? order.etaDate + 7 * 24 * 60 * 60 * 1000 : null;
              const isOverdue =
                !!due7 &&
                now > due7 &&
                order.status !== 'closed' &&
                order.status !== 'cancelled' &&
                order.status !== 'balance_received';
              const depositStep = order.checklistProgress.steps.find(
                (s) => s.stepId === 'receive_deposit',
              );
              const isDepositDone =
                depositStep?.status === 'done' || depositStep?.status === 'skipped';
              const balanceStep = order.checklistProgress.steps.find(
                (s) => s.stepId === 'receive_balance',
              );
              const isBalanceDone =
                balanceStep?.status === 'done' || balanceStep?.status === 'skipped';
              const productionStep = order.checklistProgress.steps.find(
                (s) => s.stepId === 'production',
              );
              const isProductionDone =
                productionStep?.status === 'done' || productionStep?.status === 'skipped';
              const isClosed = order.status === 'closed';
              return (
                <>
                  <PaymentStep
                    index={1}
                    title={`定金 (${order.depositPct}%)`}
                    sub="客户 → 主体 (T/T 到账)"
                    done={!!isDepositDone}
                    overdue={false}
                    money={
                      isDepositDone
                        ? fmt(
                            Math.round((order.totalAmountCents * order.depositPct) / 100),
                            order.currency,
                          )
                        : null
                    }
                  />
                  <PaymentStep
                    index={2}
                    title="主体 → 工厂"
                    sub="生产拨款 (下 PO + 工艺单)"
                    done={!!isProductionDone}
                    overdue={false}
                    money={
                      isProductionDone
                        ? fmt(Math.round(order.totalAmountCents * 0.6), order.currency)
                        : null
                    }
                  />
                  <PaymentStep
                    index={3}
                    title={`尾款 (${100 - order.depositPct}%)`}
                    sub="客户 → 主体 (验货后收取)"
                    done={!!isBalanceDone}
                    overdue={isOverdue}
                    overdueDays={
                      isOverdue && due7 ? Math.floor((now - due7) / 86400000) : undefined
                    }
                    money={
                      isBalanceDone
                        ? fmt(
                            Math.round((order.totalAmountCents * (100 - order.depositPct)) / 100),
                            order.currency,
                          )
                        : null
                    }
                  />
                  <PaymentStep
                    index={4}
                    title="退税回流"
                    sub="国税 → 主体 (财务对账)"
                    done={isClosed}
                    overdue={false}
                    money={isClosed ? '已申报' : null}
                    link="/finance/tax-refund"
                  />
                </>
              );
            })()}
          </ol>
        </Card>
      </TabPanel>

      {/* === Panel 4: 详情 (detail) === */}
      <TabPanel value="detail" active={tab === 'detail'} className="sm:block">
        <Card padding="md" className="opacity-60">
          <h2 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
            <Icon name="refresh-cw" size="sm" className="text-ink-3" />
            \u4e09\u89d2\u67b6\u6784 \u00b7 \u59d4\u6258\u5355 (v1.5)
          </h2>
          <p className="text-xs text-ink-3">
            \u59d4\u6258\u5355 (\u5185\u90e8\u8f6c\u5355) \u5c06\u5728 v1.5 \u5b9e\u73b0.
            \u5f53\u524d\u8ba2\u5355\u5047\u8bbe\u7531\u5f53\u524d\u7b7e\u7ea6\u516c\u53f8\u76f4\u63a5\u751f\u4ea7.
          </p>
        </Card>

        {order.status === 'closed' && (
          <Card padding="md" className="mt-4">
            <h2 className="text-sm font-semibold mb-3 flex items-center gap-1.5">
              <Icon name="bar-chart-3" size="sm" className="text-accent" />
              AI \u590d\u76d8\u62a5\u544a
            </h2>
            {order.postmortem ? (
              <PostmortemMarkdown text={order.postmortem} />
            ) : order.postmortemError ? (
              <div className="text-xs text-bad bg-bad/5 border border-bad rounded p-2">
                \u590d\u76d8\u751f\u6210\u5931\u8d25: {order.postmortemError}
              </div>
            ) : closeMutation.isPending ? (
              <p className="text-xs text-ink-3">
                \ud83e\udd16 AI \u6b63\u5728\u751f\u6210\u590d\u76d8\u62a5\u544a,
                \u8bf7\u7a0d\u5019\u5237\u65b0\u9875\u9762\u2026
              </p>
            ) : (
              <p className="text-xs text-ink-3">\u6682\u65e0\u590d\u76d8\u6570\u636e</p>
            )}
          </Card>
        )}
      </TabPanel>

      {toast && <Toast kind={toast.kind} message={toast.msg} onClose={() => setToast(null)} />}

      {/* v1.11: 追讨 / 信保 wizard (modal) */}
      {showCollection && (
        <CollectionWizard
          orderId={order.id}
          daysOverdue={daysOverdue}
          recommendedStage={recommendedStage}
          onToast={(kind, msg) => setToast({ kind, msg })}
          onClose={() => setShowCollection(false)}
        />
      )}
      {showSinosure && (
        <SinosureReportWizard
          orderId={order.id}
          customerId={order.customerId}
          defaultAmountUsd={order.totalAmountCents / 100}
          currency={order.currency}
          onToast={(kind, msg) => setToast({ kind, msg })}
          onClose={() => setShowSinosure(false)}
        />
      )}
    </div>
  );
}

function PostmortemMarkdown({ text }: { text: string }) {
  const lines = text.split('\n');
  const blocks: Array<{ type: 'h2' | 'p' | 'ul' | 'ol'; content: string | string[] }> = [];
  let buf: string[] = [];
  let current: 'p' | 'ul' | 'ol' | null = null;

  function flush() {
    if (current === 'ul' || current === 'ol') {
      blocks.push({ type: current, content: [...buf] });
    } else if (buf.length > 0) {
      blocks.push({ type: 'p', content: buf.join('\n') });
    }
    buf = [];
    current = null;
  }

  for (const line of lines) {
    if (line.startsWith('## ')) {
      flush();
      blocks.push({ type: 'h2', content: line.slice(3).trim() });
    } else if (/^[-*]\s+/.test(line)) {
      if (current !== 'ul') flush();
      current = 'ul';
      buf.push(line.replace(/^[-*]\s+/, ''));
    } else if (/^\d+\.\s+/.test(line)) {
      if (current !== 'ol') flush();
      current = 'ol';
      buf.push(line.replace(/^\d+\.\s+/, ''));
    } else if (line.trim() === '') {
      flush();
    } else {
      if (current === 'ul' || current === 'ol') flush();
      current = 'p';
      buf.push(line);
    }
  }
  flush();

  // 稳定 key: 用 type + content 拼接 (markdown 解析后内容稳定, 不会重排)
  // index 只作为同内容 type 出现多次时的 tie-breaker
  function blockKey(
    b: { type: 'h2' | 'p' | 'ul' | 'ol'; content: string | string[] },
    i: number,
  ): string {
    const c = Array.isArray(b.content) ? b.content.join('|') : b.content;
    return `${b.type}:${c}:${i}`;
  }

  function renderInline(s: string) {
    // key 用 part 内容 (markdown 强调段唯一, 即便重排也不冲突)
    const parts = s.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part) => {
      const isBold = part.startsWith('**') && part.endsWith('**');
      const k = isBold ? `strong:${part.slice(2, -2)}` : `text:${part}`;
      if (isBold) {
        return <strong key={k}>{part.slice(2, -2)}</strong>;
      }
      return <span key={k}>{part}</span>;
    });
  }

  return (
    <div className="prose-sm max-w-none text-ink-1">
      {blocks.map((b, i) => {
        if (b.type === 'h2') {
          return (
            <h2
              key={blockKey(b, i)}
              className="text-base font-semibold mt-4 mb-2 text-ink-1 first:mt-0"
            >
              {b.content as string}
            </h2>
          );
        }
        if (b.type === 'ul') {
          return (
            <ul key={blockKey(b, i)} className="list-disc pl-5 my-2 space-y-1 text-sm">
              {(b.content as string[]).map((item) => (
                <li key={`ul:${item}`}>{renderInline(item)}</li>
              ))}
            </ul>
          );
        }
        if (b.type === 'ol') {
          return (
            <ol key={blockKey(b, i)} className="list-decimal pl-5 my-2 space-y-1 text-sm">
              {(b.content as string[]).map((item) => (
                <li key={`ol:${item}`}>{renderInline(item)}</li>
              ))}
            </ol>
          );
        }
        return (
          <p key={blockKey(b, i)} className="text-sm leading-relaxed my-2 whitespace-pre-wrap">
            {renderInline(b.content as string)}
          </p>
        );
      })}
    </div>
  );
}
